"use client";

import { useMarket, useSiteInfo } from "@app/_context";
import { deleteCookie, hasCookie } from "cookies-next";
import dynamic from "next/dynamic";
import { useEffect } from "react";

import { CART_COOKIE } from "@/config/cart";
import {
  isAdyenCheckoutEnabled,
  isWalleyCheckoutEnabled,
} from "@/config/checkout";
import { getCartCookie } from "@/utils/cart/getCartCookie";

import { checkoutStore } from "../Checkout.store";
import { useCheckout } from "../hooks/useCheckout";
import { trackBeginCheckout } from "../utils/trackBeginCheckout";
import CheckoutContainer from "./CheckoutContainer";
import CheckoutEmpty from "./CheckoutEmpty";
import CheckoutMessage from "./CheckoutMessage";
import CheckoutSkeleton from "./CheckoutSkeleton";

const AdyenCheckout = dynamic(() => import("../adyen/AdyenCheckout"), {
  ssr: false,
  loading: () => (
    <CheckoutContainer>
      <CheckoutSkeleton />
    </CheckoutContainer>
  ),
});

const WalleyCheckout = dynamic(() => import("../walley/WalleyCheckout"), {
  ssr: false,
  loading: () => (
    <CheckoutContainer>
      <CheckoutSkeleton />
    </CheckoutContainer>
  ),
});

export default function Checkout() {
  const cartCookie = getCartCookie();
  const { data, isPending, isError, error, refetch } = useCheckout();
  const cart = data?.cart;
  const marketConfig = useMarket().state.market;
  const { minimumOrderValue } = useSiteInfo();
  const setAbleToComplete = checkoutStore((state) => state.setAbleToComplete);

  useEffect(() => {
    if (cart) {
      trackBeginCheckout(cart);
    }
  }, [cart]);

  useEffect(() => {
    if (
      error?.message?.includes("Invalid cart") ||
      error?.message?.includes("Not Found") ||
      error?.message === "CART_NOT_FOUND"
    ) {
      if (hasCookie(CART_COOKIE)) {
        deleteCookie(CART_COOKIE);
        void refetch();
      }
    }
  }, [error, refetch]);

  useEffect(() => {
    if (!cart?.items) {
      return;
    }

    const someCartItemIsSoldOut = cart.items.some(
      (item) => item.stockWatchStatus?.ableToComplete === false,
    );

    if (someCartItemIsSoldOut) {
      setAbleToComplete(false);
      return;
    }

    if (cart.totalGrossAmount === undefined) {
      setAbleToComplete(true);
      return;
    }

    const minOrderValueThreshold =
      minimumOrderValue ?? marketConfig.minimumOrderValue ?? 0;

    const belowMinimumOrderValue =
      cart.totalGrossAmount - cart.totalFreightInclVat < minOrderValueThreshold;

    setAbleToComplete(
      !belowMinimumOrderValue ||
        Boolean(cart.purchasableBelowMinimumOrderValue),
    );
  }, [
    cart?.items,
    cart?.totalGrossAmount,
    cart?.totalFreightInclVat,
    cart?.purchasableBelowMinimumOrderValue,
    marketConfig,
    minimumOrderValue,
    setAbleToComplete,
  ]);

  if (!cartCookie?.id) {
    return (
      <CheckoutContainer>
        <CheckoutEmpty />
      </CheckoutContainer>
    );
  }

  if (isPending) {
    return (
      <CheckoutContainer>
        <CheckoutSkeleton />
      </CheckoutContainer>
    );
  }

  if (isError || !cart?.items.length) {
    return (
      <CheckoutContainer>
        <CheckoutEmpty />
      </CheckoutContainer>
    );
  }

  if (isAdyenCheckoutEnabled({ marketConfig })) {
    return <AdyenCheckout />;
  }

  if (isWalleyCheckoutEnabled({ marketConfig })) {
    return <WalleyCheckout />;
  }

  return (
    <CheckoutContainer>
      <CheckoutMessage type="error">Unknown checkout</CheckoutMessage>
    </CheckoutContainer>
  );
}
